import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  Discount,
  ListDiscountsInput,
  ListDiscountsResponse,
  GetListPricesInput,
  ListPriceResult,
  SearchDiscountScopesInput,
} from '@erp/rpc';

/**
 * Hook for fetching discounts with optional filters
 */
export function useDiscountsList(filters: ListDiscountsInput = {}) {
  const query = useQuery(
    orpc.accountsReceivable.discounts.list.queryOptions({
      input: filters,
    }),
  );

  const discounts = query.data?.discounts ?? [];
  const pagination = query.data?.pagination;
  // React Query v5 can briefly have status='pending' with isFetching=false
  // during initialization. Use isPendingOrLoading to ensure loading state
  // is shown until data actually arrives.
  const isPendingOrLoading = query.isLoading || query.status === 'pending';

  return {
    ...query,
    isPendingOrLoading,
    discounts,
    pagination,
  } as typeof query & {
    data?: ListDiscountsResponse;
    discounts: Discount[];
    pagination: ListDiscountsResponse['pagination'] | undefined;
    isPendingOrLoading: boolean;
  };
}

/**
 * Hook for fetching list prices for SKUs of a single customer
 */
export function useListPrices(input: GetListPricesInput | null) {
  return useQuery({
    ...orpc.accountsReceivable.discounts.getListPrices.queryOptions({
      input: input ?? { customerId: '', skus: [] },
    }),
    // `enabled` must sit at the top level (a nested `query` key is ignored),
    // or the placeholder empty input fires and 422s against the contract.
    enabled: !!input && input.skus.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Input for multi-customer list price fetching
 */
export interface CustomerSkusInput {
  customerId: string;
  skus: string[];
}

/**
 * Map of "customerId:sku" → ListPriceResult
 */
export type ListPricesMap = Map<string, ListPriceResult | null>;

/**
 * Hook for fetching list prices across multiple customers in parallel.
 * Returns a merged map keyed by "customerId:sku".
 */
export function useMultiCustomerListPrices(inputs: CustomerSkusInput[]) {
  const queries = useQueries({
    queries: inputs
      .filter((input) => input.skus.length > 0)
      .map((input) =>
        orpc.accountsReceivable.discounts.getListPrices.queryOptions({
          input: {
            customerId: input.customerId,
            skus: input.skus,
          },
          query: {
            staleTime: 5 * 60 * 1000,
          },
        }),
      ),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isInitialLoading = queries.some(
    (q) => q.isLoading && !q.isFetchedAfterMount,
  );

  // Merge all results into a single map keyed by "customerId:sku"
  const pricesMap: ListPricesMap = useMemo(() => {
    const map: ListPricesMap = new Map();
    const validInputs = inputs.filter((input) => input.skus.length > 0);

    for (let i = 0; i < queries.length; i++) {
      const query = queries[i];
      const input = validInputs[i];
      if (!query?.data || !input) continue;

      const prices = query.data.prices;
      for (const [sku, result] of Object.entries(prices)) {
        map.set(`${input.customerId}:${sku}`, result);
      }
    }

    return map;
  }, [queries, inputs]);

  return {
    pricesMap,
    isLoading,
    isInitialLoading,
  };
}

/**
 * Hook for fetching available PRODVIEW categories and product lines
 */
export function useAvailableCategories() {
  return useQuery(
    orpc.accountsReceivable.discounts.getAvailableCategories.queryOptions({
      query: {
        staleTime: 10 * 60 * 1000, // 10 minutes — categories don't change often
      },
    }),
  );
}

/**
 * Hook for fetching distinct category/product line values for filter dropdowns.
 */
export function useDiscountFilterOptions() {
  return useQuery(
    orpc.accountsReceivable.discounts.filterOptions.queryOptions({
      query: {
        staleTime: 10 * 60 * 1000, // 10 minutes — these change infrequently
      },
    }),
  );
}

/**
 * Hook for search-as-you-type in SKU and customer filter dropdowns.
 */
export function useSearchDiscountScopes(params: SearchDiscountScopesInput) {
  return useQuery(
    orpc.accountsReceivable.discounts.searchScopes.queryOptions({
      input: params,
      query: {
        enabled: params.query.length > 0,
      },
    }),
  );
}

/**
 * Prefix key that matches every `discounts.list` cache entry regardless of
 * filter input. Used to invalidate all list variants after a mutation.
 */
const discountsListKey = () => orpc.accountsReceivable.discounts.list.key();

/**
 * Hook for deleting a discount
 */
export function useDeleteDiscount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: string }) => {
      return await rpcClient.accountsReceivable.discounts.delete(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: discountsListKey() });
    },
  });
}

/**
 * Hook for disabling a discount (one-way; not reversible via API).
 * Gated server-side by Action.Update on Subject.Discount.
 */
export function useDisableDiscount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: string }) => {
      return await rpcClient.accountsReceivable.discounts.disable(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: discountsListKey() });
    },
  });
}
